import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/AdminStorePage.jsx - COMPLETO CON GESTIÓN DE KITS
import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    Button,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Inventory as ProductIcon,
    Category as CategoryIcon,
    LocalOffer as CouponIcon,
    ShoppingCart as OrderIcon,
    Analytics as AnalyticsIcon,
    Inventory2 as KitIcon,
    Archive as ArchiveIcon
} from '@mui/icons-material';

import { ecommerceApi, cartHelpers } from '../services/ecommerceApi';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../components/common/GlobalNotifications';
import ProductForm from '../components/admin/ProductForm';
import CategoryForm from '../components/admin/CategoryForm';
import CouponForm from '../components/admin/CouponForm';
import KitForm from '../components/admin/KitForm';
import OrderManagement from '../components/admin/OrderManagement';

const AdminStorePage = () => {
  const { t } = useTranslation();
    const { user, hasPermission } = useAuth();
    const { showSuccess, showError } = useNotification();

    const isArsenalAdmin = user?.email?.endsWith(t('hardcoded.components_common_sidebar.arsenalmx')) && hasPermission('admin');

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [tabErrors, setTabErrors] = useState({});

    // Products state
    const [products, setProducts] = useState([]);
    const [productDialogOpen, setProductDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Categories state
    const [categories, setCategories] = useState([]);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Kits state
    const [kits, setKits] = useState([]);
    const [kitDialogOpen, setKitDialogOpen] = useState(false);
    const [selectedKit, setSelectedKit] = useState(null);

    // Coupons state
    const [coupons, setCoupons] = useState([]);
    const [couponDialogOpen, setCouponDialogOpen] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    // Orders state
    const [orders, setOrders] = useState([]);

    const emptyDashboardData = {
        totals: {
            products: 0,
            orders: 0,
            pending_orders: 0,
            total_revenue: 0,
            weekly_revenue: 0,
            monthly_revenue: 0
        },
        recent_orders: [],
        low_stock_products: []
    };

    useEffect(() => {
        if (!isArsenalAdmin) {
            showError(t('hardcoded.pages_adminstorepage.acceso_restringido_al_personal_de'));
            return;
        }

        loadDashboardData();
    }, [isArsenalAdmin]);

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        if (activeTab === 0) loadDashboardData();
        else if (activeTab === 1) loadProducts();
        else if (activeTab === 2) loadCategories();
        else if (activeTab === 3) loadKits();
        else if (activeTab === 4) loadCoupons();
        else if (activeTab === 5) loadOrders();
    }, [activeTab]);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const response = await ecommerceApi.admin.dashboard();
            setDashboardData(response.data || emptyDashboardData);
            setTabErrors((prev) => ({ ...prev, 0: null }));
        } catch (error) {
            console.error(t('hardcoded.pages_adminstorepage.error_loading_dashboard'), error);
            setDashboardData(emptyDashboardData);
            setTabErrors((prev) => ({ ...prev, 0: t('hardcoded.pages_adminstorepage.no_fue_posible_cargar_datos') }));
        } finally {
            setLoading(false);
        }
    };

    const loadProducts = async () => {
        setLoading(true);
        try {
            const response = await ecommerceApi.admin.products.getAll({
                include_inactive: true,
                per_page: 100
            });
            const nextProducts = Array.isArray(response.data) ? response.data : (response.data?.items || []);
            setProducts(nextProducts);
            setTabErrors((prev) => ({ ...prev, 1: null }));
        } catch (error) {
            console.error(t('hardcoded.components_admin_kitform.error_loading_products'), error);
            setProducts([]);
            setTabErrors((prev) => ({ ...prev, 1: t('hardcoded.pages_adminstorepage.no_fue_posible_cargar_productos') }));
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await ecommerceApi.admin.categories.getAll();
            setCategories(response.data || []);
            setTabErrors((prev) => ({ ...prev, 2: null }));
        } catch (error) {
            console.error(t('hardcoded.pages_adminstorepage.error_loading_categories'), error);
            setCategories([]);
            setTabErrors((prev) => ({ ...prev, 2: t('hardcoded.pages_adminstorepage.no_fue_posible_cargar_categorias') }));
        }
    };
    const loadKits = async () => {
        setLoading(true);
        try {
            const response = await ecommerceApi.admin.kits.getAll(true);
            setKits(response.data || []);
            setTabErrors((prev) => ({ ...prev, 3: null }));
        } catch (error) {
            console.error(t('hardcoded.pages_adminstorepage.error_loading_kits'), error);
            setKits([]);
            setTabErrors((prev) => ({ ...prev, 3: t('hardcoded.pages_adminstorepage.no_fue_posible_cargar_kits') }));
        } finally {
            setLoading(false);
        }
    };

    const loadCoupons = async () => {
        setLoading(true);
        try {
            const response = await ecommerceApi.admin.coupons.getAll();
            setCoupons(response.data || []);
            setTabErrors((prev) => ({ ...prev, 4: null }));
        } catch (error) {
            console.error(t('hardcoded.pages_adminstorepage.error_loading_coupons'), error);
            setCoupons([]);
            setTabErrors((prev) => ({ ...prev, 4: t('hardcoded.pages_adminstorepage.no_fue_posible_cargar_cupones') }));
        } finally {
            setLoading(false);
        }
    };

    const loadOrders = async () => {
        setLoading(true);
        try {
            const response = await ecommerceApi.admin.orders.getAll({
                limit: 100
            });
            setOrders(response.data || []);
            setTabErrors((prev) => ({ ...prev, 5: null }));
        } catch (error) {
            console.error('Error loading orders:', error);
            setOrders([]);
            setTabErrors((prev) => ({ ...prev, 5: t('hardcoded.pages_adminstorepage.no_fue_posible_cargar_ordenes') }));
        } finally {
            setLoading(false);
        }
    };

    // Product handlers
    const handleProductSave = async (productData) => {
        try {
            if (selectedProduct) {
                await ecommerceApi.admin.products.update(selectedProduct.id, productData);
                showSuccess(t('hardcoded.pages_adminstorepage.producto_actualizado'));
            } else {
                await ecommerceApi.admin.products.create(productData);
                showSuccess(t('hardcoded.pages_adminstorepage.producto_creado'));
            }

            setProductDialogOpen(false);
            setSelectedProduct(null);
            loadProducts();
        } catch (error) {
            showError(error.response?.data?.detail || t('hardcoded.pages_adminstorepage.error_al_guardar_producto'));
        }
    };

    const handleProductDelete = async (productId) => {
        if (window.confirm(t('hardcoded.pages_adminstorepage.estas_seguro_de_desactivar_este'))) {
            try {
                await ecommerceApi.admin.products.delete(productId);
                showSuccess(t('hardcoded.pages_adminstorepage.producto_desactivado'));
                loadProducts();
            } catch (error) {
                showError(t('hardcoded.pages_adminstorepage.error_al_desactivar_producto'));
            }
        }
    };

    // Category handlers
    const handleCategorySave = async (categoryData) => {
        try {
            if (selectedCategory) {
                await ecommerceApi.admin.categories.update(selectedCategory.id, categoryData);
                showSuccess(t('hardcoded.pages_adminstorepage.categoria_actualizada'));
            } else {
                await ecommerceApi.admin.categories.create(categoryData);
                showSuccess(t('hardcoded.pages_adminstorepage.categoria_creada'));
            }

            setCategoryDialogOpen(false);
            setSelectedCategory(null);
            loadCategories();
        } catch (error) {
            showError(error.response?.data?.detail || t('hardcoded.pages_adminstorepage.error_al_guardar_categoria'));
        }
    };

    // Kit handlers
    const handleKitSave = async (kitData) => {
        try {
            if (selectedKit) {
                await ecommerceApi.admin.kits.update(selectedKit.id, kitData);
                showSuccess(t('hardcoded.pages_adminstorepage.kit_actualizado'));
            } else {
                await ecommerceApi.admin.kits.create(kitData);
                showSuccess(t('hardcoded.pages_adminstorepage.kit_creado'));
            }

            setKitDialogOpen(false);
            setSelectedKit(null);
            loadKits();
        } catch (error) {
            showError(error.response?.data?.detail || t('hardcoded.pages_adminstorepage.error_al_guardar_kit'));
        }
    };

    const handleKitDelete = async (kitId) => {
        if (window.confirm(t('hardcoded.pages_adminstorepage.estas_seguro_de_desactivar_este'))) {
            try {
                await ecommerceApi.admin.kits.delete(kitId);
                showSuccess(t('hardcoded.pages_adminstorepage.kit_desactivado'));
                loadKits();
            } catch (error) {
                showError(t('hardcoded.pages_adminstorepage.error_al_desactivar_kit'));
            }
        }
    };

    // Coupon handlers
    const handleCouponSave = async (couponData) => {
        try {
            if (selectedCoupon) {
                await ecommerceApi.admin.coupons.update(selectedCoupon.id, couponData);
                showSuccess(t('hardcoded.pages_adminstorepage.cupon_actualizado'));
            } else {
                await ecommerceApi.admin.coupons.create(couponData);
                showSuccess(t('hardcoded.pages_adminstorepage.cupon_creado'));
            }

            setCouponDialogOpen(false);
            setSelectedCoupon(null);
            loadCoupons();
        } catch (error) {
            showError(error.response?.data?.detail || t('hardcoded.pages_adminstorepage.error_al_guardar_cupon'));
        }
    };

    if (!isArsenalAdmin) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Alert severity="error">
                    <Typography variant="h5" gutterBottom>
                        {i18n.t('hardcoded.pages_adminstorepage.acceso_restringido')}</Typography>
                    <Typography>
                        {t('hardcoded.pages_adminstorepage.esta_seccion_esta_reservada_unicamente')}</Typography>
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" fontWeight={700} color="primary" gutterBottom>
                    {t('hardcoded.pages_adminstorepage.panel_de_administracion_-_tienda')}</Typography>
                <Typography variant="h6" color="text.secondary">
                    {t('hardcoded.pages_adminstorepage.gestion_completa_de_productos_kits')}</Typography>
            </Box>

            {/* Navigation Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab icon={<AnalyticsIcon />} label={t('hardcoded.pages_adminstorepage.dashboard')} />
                    <Tab icon={<ProductIcon />} label={t('cart.items')} />
                    <Tab icon={<CategoryIcon />} label={t('hardcoded.pages_adminstorepage.categorias')} />
                    <Tab icon={<KitIcon />} label={t('pages_adminstorepage.tab_kits')} />
                    <Tab icon={<CouponIcon />} label={t('hardcoded.pages_adminstorepage.cupones')} />
                    <Tab icon={<OrderIcon />} label={t('hardcoded.pages_adminstorepage.ordenes')} />
                </Tabs>
            </Paper>

            {/* Content */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={60} />
                </Box>
            ) : (
                <>
                    {tabErrors[activeTab] && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {tabErrors[activeTab]}
                        </Alert>
                    )}

                    {/* Dashboard Tab */}
                    {activeTab === 0 && (dashboardData || emptyDashboardData) && (
                        <Grid container spacing={3}>
                            {/* Stats Cards */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h4" color="primary" fontWeight={600}>
                                            {(dashboardData || emptyDashboardData).totals.products}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {i18n.t('hardcoded.pages_adminstorepage.productos_activos')}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h4" color="success.main" fontWeight={600}>
                                            {(dashboardData || emptyDashboardData).totals.orders}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('hardcoded.pages_adminstorepage.total_ordenes')}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h4" color="warning.main" fontWeight={600}>
                                            {(dashboardData || emptyDashboardData).totals.pending_orders}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('hardcoded.pages_adminstorepage.ordenes_pendientes')}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h4" color="primary" fontWeight={600}>
                                            {cartHelpers.formatCurrency((dashboardData || emptyDashboardData).totals.total_revenue)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {i18n.t('hardcoded.pages_adminstorepage.revenue_total')}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Revenue Cards */}
                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {i18n.t('hardcoded.pages_adminstorepage.ingresos_semanales')}</Typography>
                                        <Typography variant="h4" color="success.main" fontWeight={600}>
                                            {cartHelpers.formatCurrency((dashboardData || emptyDashboardData).totals.weekly_revenue)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {i18n.t('hardcoded.pages_adminstorepage.ingresos_mensuales')}</Typography>
                                        <Typography variant="h4" color="success.main" fontWeight={600}>
                                            {cartHelpers.formatCurrency((dashboardData || emptyDashboardData).totals.monthly_revenue)}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Recent Orders */}
                            <Grid item xs={12} lg={8}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {t('hardcoded.pages_adminstorepage.ordenes_recientes')}</Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>{t('hardcoded.pages_adminstorepage.numero')}</TableCell>
                                                        <TableCell>{i18n.t('hardcoded.components_admin_ordermanagement.cliente')}</TableCell>
                                                        <TableCell>{i18n.t('common.total')}</TableCell>
                                                        <TableCell>{i18n.t('gps.table.status')}</TableCell>
                                                        <TableCell>Fecha</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {((dashboardData || emptyDashboardData).recent_orders || []).map((order) => (
                                                        <TableRow key={order.id}>
                                                            <TableCell>{order.order_number}</TableCell>
                                                            <TableCell>{order.user?.name}</TableCell>
                                                            <TableCell>{cartHelpers.formatCurrency(order.total)}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={order.status_display}
                                                                    size="small"
                                                                    color={order.status === 'pending' ? 'warning' : 'success'}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {new Date(order.created_at).toLocaleDateString()}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Low Stock Products */}
                            <Grid item xs={12} lg={4}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {t('hardcoded.pages_adminstorepage.stock_bajo')}</Typography>
                                        {((dashboardData || emptyDashboardData).low_stock_products || []).length > 0 ? (
                                            ((dashboardData || emptyDashboardData).low_stock_products || []).map((product) => (
                                                <Box key={product.id} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {product.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="error">
                                                        {t('hardcoded.pages_adminstorepage.stock')}{product.stock_quantity}
                                                    </Typography>
                                                </Box>
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {t('hardcoded.pages_adminstorepage.no_hay_productos_con_stock')}</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {/* Products Tab */}
                    {activeTab === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="h5" fontWeight={600}>
                                    {t('hardcoded.pages_adminstorepage.gestion_de_productos')}</Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setSelectedProduct(null);
                                        setProductDialogOpen(true);
                                    }}
                                >
                                    {t('hardcoded.pages_adminstorepage.nuevo_producto')}</Button>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{i18n.t('hardcoded.pages_adminstorepage.imagen')}</TableCell>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>{i18n.t('product.price')}</TableCell>
                                            <TableCell>{t('product.stock')}</TableCell>
                                            <TableCell>{i18n.t('gps.table.status')}</TableCell>
                                            <TableCell>{t('common.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {products.map((product) => (
                                            <TableRow key={product.id}>
                                                <TableCell>
                                                    <Box
                                                        component="img"
                                                        src={product.main_image_url || '/images/placeholder-product.jpg'}
                                                        alt={product.name}
                                                        sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {product.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {product.category?.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{product.sku}</TableCell>
                                                <TableCell>{cartHelpers.formatCurrency(product.price)}</TableCell>
                                                <TableCell>
                                                    {product.track_inventory ? (
                                                        <Chip
                                                            label={product.stock_quantity}
                                                            size="small"
                                                            color={product.stock_quantity <= 10 ? 'error' : 'success'}
                                                        />
                                                    ) : (
                                                        t('hardcoded.pages_adminstorepage.no_rastreado')
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={product.is_active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                                                        size="small"
                                                        color={product.is_active ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedProduct(product);
                                                            setProductDialogOpen(true);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleProductDelete(product.id)}
                                                    >
                                                        <ArchiveIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Categories Tab */}
                    {activeTab === 2 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="h5" fontWeight={600}>
                                    {t('hardcoded.pages_adminstorepage.gestion_de_categorias')}</Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setSelectedCategory(null);
                                        setCategoryDialogOpen(true);
                                    }}
                                >
                                    {t('hardcoded.pages_adminstorepage.nueva_categoria')}</Button>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell>Slug</TableCell>
                                            <TableCell>{i18n.t('gps.table.status')}</TableCell>
                                            <TableCell>{i18n.t('hardcoded.pages_adminstorepage.orden')}</TableCell>
                                            <TableCell>{t('common.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {categories.map((category) => (
                                            <TableRow key={category.id}>
                                                <TableCell>{category.name}</TableCell>
                                                <TableCell>{category.slug}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={category.is_active ? t('hardcoded.components_ai_aimemoryeditor.activa') : t('hardcoded.components_ai_aimemoryeditor.inactiva')}
                                                        size="small"
                                                        color={category.is_active ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell>{category.sort_order}</TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedCategory(category);
                                                            setCategoryDialogOpen(true);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Kits Tab */}
                    {activeTab === 3 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="h5" fontWeight={600}>
                                    {t('hardcoded.pages_adminstorepage.gestion_de_kits_de_productos')}</Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setSelectedKit(null);
                                        setKitDialogOpen(true);
                                    }}
                                >
                                    {i18n.t('hardcoded.pages_adminstorepage.nuevo_kit')}</Button>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{i18n.t('hardcoded.pages_adminstorepage.imagen')}</TableCell>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell>SKU</TableCell>
                                            <TableCell>{i18n.t('cart.items')}</TableCell>
                                            <TableCell>{i18n.t('product.price')}</TableCell>
                                            <TableCell>{i18n.t('hardcoded.pages_adminstorepage.ahorro')}</TableCell>
                                            <TableCell>{i18n.t('gps.table.status')}</TableCell>
                                            <TableCell>{t('common.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {kits.map((kit) => (
                                            <TableRow key={kit.id}>
                                                <TableCell>
                                                    <Box
                                                        component="img"
                                                        src={kit.main_image || '/images/kit-placeholder.jpg'}
                                                        alt={kit.name}
                                                        sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {kit.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{kit.sku}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={`${kit.total_items || kit.products?.length || 0} items`}
                                                        size="small"
                                                        color="info"
                                                    />
                                                </TableCell>
                                                <TableCell>{cartHelpers.formatCurrency(kit.price)}</TableCell>
                                                <TableCell>
                                                    {kit.savings_amount > 0 ? (
                                                        <Chip
                                                            label={cartHelpers.formatCurrency(kit.savings_amount)}
                                                            size="small"
                                                            color="success"
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={kit.is_active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                                                        size="small"
                                                        color={kit.is_active ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedKit(kit);
                                                            setKitDialogOpen(true);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleKitDelete(kit.id)}
                                                    >
                                                        <ArchiveIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Coupons Tab */}
                    {activeTab === 4 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                <Typography variant="h5" fontWeight={600}>
                                    {t('hardcoded.pages_adminstorepage.gestion_de_cupones')}</Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        setSelectedCoupon(null);
                                        setCouponDialogOpen(true);
                                    }}
                                >
                                    {t('hardcoded.pages_adminstorepage.nuevo_cupon')}</Button>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('hardcoded.pages_adminstorepage.codigo')}</TableCell>
                                            <TableCell>Nombre</TableCell>
                                            <TableCell>{i18n.t('vehicles.form.type')}</TableCell>
                                            <TableCell>{t('hardcoded.components_admin_couponform.aplica_sobre')}</TableCell>
                                            <TableCell>{i18n.t('hardcoded.pages_adminstorepage.descuento')}</TableCell>
                                            <TableCell>{i18n.t('hardcoded.pages_adminstorepage.usos')}</TableCell>
                                            <TableCell>{t('hardcoded.components_admin_couponform.valido_hasta')}</TableCell>
                                            <TableCell>{i18n.t('gps.table.status')}</TableCell>
                                            <TableCell>{t('common.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {coupons.map((coupon) => (
                                            <TableRow key={coupon.id}>
                                                <TableCell>
                                                    <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                                                        {coupon.code}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{coupon.name}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={coupon.discount_type === 'percentage' ? t('hardcoded.components_admin_couponform.porcentaje') : 'Fijo'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={coupon.applies_to || (coupon.discount_type === 'fixed' ? t('common.total') : t('hardcoded.components_admin_couponform.subtotal'))}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {coupon.discount_type === 'percentage' ?
                                                        `${coupon.discount_value}%` :
                                                        cartHelpers.formatCurrency(coupon.discount_value)
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {coupon.usage_count}
                                                    {coupon.usage_limit && ` / ${coupon.usage_limit}`}
                                                </TableCell>
                                                <TableCell>
                                                    {coupon.valid_until ?
                                                        new Date(coupon.valid_until).toLocaleDateString() :
                                                        t('hardcoded.components_alarms_alarmlist.sin_limite')
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={coupon.is_valid ? t('hardcoded.pages_adminstorepage.valido') : t('hardcoded.pages_adminstorepage.invalido')}
                                                        size="small"
                                                        color={coupon.is_valid ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedCoupon(coupon);
                                                            setCouponDialogOpen(true);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Orders Tab */}
                    {activeTab === 5 && (
                        <OrderManagement orders={orders} onOrderUpdate={loadOrders} />
                    )}
                </>
            )}

            {/* Dialogs */}
            <ProductForm
                open={productDialogOpen}
                onClose={() => {
                    setProductDialogOpen(false);
                    setSelectedProduct(null);
                }}
                onSave={handleProductSave}
                product={selectedProduct}
                categories={categories}
            />

            <CategoryForm
                open={categoryDialogOpen}
                onClose={() => {
                    setCategoryDialogOpen(false);
                    setSelectedCategory(null);
                }}
                onSave={handleCategorySave}
                category={selectedCategory}
                categories={categories}
            />

            <KitForm
                open={kitDialogOpen}
                onClose={() => {
                    setKitDialogOpen(false);
                    setSelectedKit(null);
                }}
                onSave={handleKitSave}
                kit={selectedKit}
            />

            <CouponForm
                open={couponDialogOpen}
                onClose={() => {
                    setCouponDialogOpen(false);
                    setSelectedCoupon(null);
                }}
                onSave={handleCouponSave}
                coupon={selectedCoupon}
            />
        </Container>
    );
};

export default AdminStorePage;